/**
 * @fileoverview The sheets of "Merge into..." (D66): the prompt after an edit that made twins, the
 * pick step (two or more twins), and the confirm sheet that merges.
 *
 * The backend decides and the app displays. The twins are the ids the server sent with the
 * plants; what would move, the variety question and every count are the server's merge preview
 * (api/plantMerge), drawn as sent. The sheet composes only its own sentence frames
 * (plants.merge.*) around the server's names.
 *
 * Steps. The ask prompt (mode 'ask', raised by the edit's caller after the form has closed) has
 * Keep both on the left and Merge... on the right; Merge... only opens the next step, nothing moves. The pick step lists
 * the twins and is skipped when there is one (firstMergeStep). The confirm sheet draws Goes ->
 * Stays, the variety rows (when the server asks), what moves, "Nothing is deleted", a full-width
 * Merge on the right with Cancel on the left, in an ActionRow (B111; Button loading while it merges). It cannot be closed while merging.
 *
 * After a merge: a toast naming both plants (no Undo), the sheet closes and onMerged tells the
 * caller which plant stayed so the list can scroll to it. The kept plant's notes and share-count
 * queries are invalidated; the list itself is refetched by the context (mergePlantInto).
 *
 * A failure on our side shows the standard sentence with the support address as a link, never
 * "try again"; a refusal (400, 404, 409) shows the server's own sentence. Both leave Cancel.
 *
 * Where: PlantGrid and PlantQuickLook, through lib/useMergeFlow.
 * API: GET /plants/:id/merge-preview and POST /plants/:id/merge (api/plantMerge) through
 * PlantsContext.mergePlantInto.
 * Words (en/es): plants.merge.*; plants.form.fields.notSure; plants.card.notesCount*.
 *
 * @module MergePlantSheet
 */

import React from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import Spinner from '@/shared/components/ui/Spinner';
import { toast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { getMergePreview, type MergePreview } from '@/shared/api/plantMerge';
import { refusalWords } from '@/shared/lib/errorMessage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { FailureText } from '@/shared/components/ServerFailure';
import type { Plant } from '@/shared/types/plant';
import { cn } from '@/lib/utils';
import {
  addedDay,
  afterMergeKeys,
  failureKind,
  firstMergeStep,
  mergeName,
  movesView,
  pickRows,
  previewKey,
  varietyRows,
  varietyToSend,
  type EditAsk,
} from '../../lib/mergePlants';

/** What the sheet is opened for. */
export interface MergePlantSheetProps {
  /** The plant that goes; the sheet is open while this is set. */
  plant: Plant | null;
  /** Its twins, as plants of the list. */
  twins: Plant[];
  /** 'merge' opens at the pick or confirm step; 'ask' opens at the prompt raised after an edit. */
  mode: 'merge' | 'ask';
  /** Asks to close; ignored while a merge is on its way. */
  onClose: () => void;
  /** Called once the merge is done, after the sheet asked to close, with the plant that stayed. */
  onMerged: (keptPlantObjId: number) => void;
}

/** Bottom sheet on a phone, a small centred box from sm up (as ArchiveOrDeleteDialog). */
const SHEET_CLASS = cn(
  'sm:max-w-sm',
  'max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-h-[90dvh] max-sm:translate-x-0 max-sm:rounded-t-xl max-sm:border-x-0 max-sm:border-b-0 max-sm:px-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:pt-5',
  'max-sm:data-[state=open]:slide-in-from-bottom-8 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:zoom-in-100',
);

/** A row the thumb can hit: at least 48px tall. */
const ROW = 'flex min-h-12 w-full items-center gap-3 rounded-md border bg-background px-3 py-2 text-left';

/**
 * The sentence for a failed request: the server's own for a refusal, the standard one (reported to
 * Sentry, with the support address) for a failure on our side or no answer.
 *
 * @param error - Whatever was thrown.
 * @param fallback - Said when the server gave no sentence.
 * @returns The sentence.
 */
function failureSentence(error: unknown, fallback: string): string {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (failureKind(status) === 'refusal') {
    return (axios.isAxiosError(error) ? refusalWords(error.response?.data) : null) ?? fallback;
  }
  return serverFailureText(error, 'plantMerge') ?? fallback;
}

/**
 * A server line with its leading count set in the accent ink ("3 to-do entries").
 *
 * @param props - The line as the server worded it.
 */
const MoveLine: React.FC<{ line: string }> = ({ line }) => {
  const match = /^(\d+)(.*)$/.exec(line);
  if (!match) return <li>{line}</li>;
  return (
    <li>
      <span className="font-bold tabular-nums text-accent-ink">{match[1]}</span>
      {match[2]}
    </li>
  );
};

/** What the ask prompt needs. */
interface AskStepProps {
  /** The plant that goes. */
  plant: Plant;
  /** The prompt's variant and twins. */
  ask: EditAsk;
  /** Opens the next step. */
  onMerge: () => void;
  /** Keeps both: closes, changes nothing. */
  onKeep: () => void;
}

/**
 * The prompt after an edit that made twins: one line and two buttons.
 *
 * @param props - AskStepProps.
 */
const AskStep: React.FC<AskStepProps> = ({ plant, ask, onMerge, onKeep }) => {
  const { reactTranslate: t } = useTranslation();
  const one = ask.variant === 'one';
  const words = { crop: plant.names.crop, crops: plant.names.crops, bed: plant.location.name, name: mergeName(plant), count: ask.count };
  return (
    <>
      <DialogHeader className="text-left">
        <DialogTitle>{t(one ? 'plants.merge.askOneTitle' : 'plants.merge.askManyTitle', words)}</DialogTitle>
        <DialogDescription>{t(one ? 'plants.merge.askOneBody' : 'plants.merge.askManyBody', words)}</DialogDescription>
      </DialogHeader>
      <ActionRow onCancel={onKeep} cancelLabel={t(one ? 'plants.merge.askKeepBoth' : 'plants.merge.askKeepAll')}>
        <Button onClick={onMerge}>{t('plants.merge.askMerge')}</Button>
      </ActionRow>
    </>
  );
};

/** What the pick step needs. */
interface PickStepProps {
  /** The plant that goes. */
  plant: Plant;
  /** Its twins. */
  twins: Plant[];
  /** Picks the twin that stays. */
  onPick: (twinId: number) => void;
  /** Cancels. */
  onCancel: () => void;
}

/**
 * The pick step: one row per twin, saying that it stays, with its photos, notes and the day added.
 *
 * @param props - PickStepProps.
 */
const PickStep: React.FC<PickStepProps> = ({ plant, twins, onPick, onCancel }) => {
  const { reactTranslate: t, i18n } = useTranslation();
  /** A day as the form writes dates: short month and day, in the reader's language. */
  const formatDay = (value: string): string => addedDay(value, i18n.language);
  const rows = pickRows(twins, formatDay);
  const name = mergeName(plant);
  // The server's name already carries the day when a twin shares the name, so the title adds none.
  const title = t('plants.merge.pickTitle', { name });
  return (
    <>
      <DialogHeader className="text-left">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {t('plants.merge.pickSub', { crops: plant.names.crops, bed: plant.location.name })}
        </DialogDescription>
      </DialogHeader>
      <ul className="max-h-[50dvh] space-y-2 overflow-y-auto">
        {rows.map((row) => (
          <li key={row.plantObjId}>
            <button type="button" onClick={() => onPick(row.plantObjId)} className={cn(ROW, 'flex-col items-start gap-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}>
              <span className="text-[0.9375rem] font-medium">{row.name}</span>
              <span className="text-xs text-muted-foreground">
                {[
                  t('plants.merge.pickStays'),
                  t(row.photos === 1 ? 'plants.merge.pickPhotosOne' : 'plants.merge.pickPhotos', { count: row.photos }),
                  t(row.notes === 1 ? 'plants.card.notesCountOne' : 'plants.card.notesCount', { count: row.notes }),
                  row.added ? t('plants.merge.pickAdded', { date: row.added }) : '',
                ].filter(Boolean).join(' · ')}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {/* A tap on a twin's row picks it, so this foot has no action beside Cancel. */}
      <ActionRow onCancel={onCancel} cancelLabel={t('plants.merge.cancel')}>{null}</ActionRow>
    </>
  );
};

/** What the confirm sheet needs. */
interface ConfirmStepProps {
  /** The plant that goes. */
  gone: Plant;
  /** The plant that stays. */
  keep: Plant;
  /** Asks the sheet to close (Cancel, or after the merge). */
  onClose: () => void;
  /** Called with the kept plant's id once the merge is done. */
  onMerged: (keptPlantObjId: number) => void;
  /** Reports whether the merge is on its way, so the sheet cannot be closed under it. */
  onBusy: (busy: boolean) => void;
}

/**
 * The confirm sheet: the pair, the variety rows, what moves, the reassurance, Merge and Cancel.
 *
 * @param props - ConfirmStepProps.
 */
const ConfirmStep: React.FC<ConfirmStepProps> = ({ gone, keep, onClose, onMerged, onBusy }) => {
  const { reactTranslate: t } = useTranslation();
  const { mergePlantInto } = usePlantsContext();
  const queryClient = useQueryClient();
  const goneId = gone.plant_obj_id;
  const keepId = keep.plant_obj_id;
  const { data: preview, error: loadError, isPending } = useQuery<MergePreview>({
    queryKey: previewKey(goneId, keepId),
    queryFn: () => getMergePreview(goneId, keepId),
    retry: false,
    gcTime: 0,
  });
  /** The variety row the gardener tapped, or null to leave the server's suggestion. */
  const [picked, setPicked] = React.useState<number | null>(null);
  /** True while the merge is on its way. */
  const [merging, setMerging] = React.useState(false);
  /** Why the merge failed, said on the sheet. */
  const [error, setError] = React.useState<string | null>(null);

  const rows = preview ? varietyRows(preview.varietyChoice) : null;
  const selectedId = preview ? varietyToSend(preview.varietyChoice, picked) : null;
  const moves = preview ? movesView(preview) : null;

  /** Merges, then toasts, closes and tells the caller which plant stayed. */
  const handleMerge = async (): Promise<void> => {
    if (!preview) return;
    setMerging(true);
    onBusy(true);
    setError(null);
    try {
      await mergePlantInto(goneId, keepId, varietyToSend(preview.varietyChoice, picked));
      for (const key of afterMergeKeys(keepId).invalidate) void queryClient.invalidateQueries({ queryKey: key });
      toast({ title: t('plants.merge.toast', { gone: preview.gone.name, keep: preview.keep.name }) });
      onBusy(false);
      onClose();
      onMerged(keepId);
    } catch (err) {
      setError(failureSentence(err, t('plants.merge.failedTitle')));
      setMerging(false);
      onBusy(false);
    }
  };

  const failed = error ?? (loadError ? failureSentence(loadError, t('plants.merge.loadFailed')) : null);
  return (
    <>
      <DialogHeader className="text-left">
        <DialogTitle>
          {error || loadError ? t('plants.merge.failedTitle') : t('plants.merge.confirmTitle', { crops: preview?.crops ?? gone.names.crops })}
        </DialogTitle>
        <DialogDescription className="sr-only">{mergeName(gone)} / {mergeName(keep)}</DialogDescription>
      </DialogHeader>

      {isPending && <div className="flex justify-center py-6"><Spinner size={20} /></div>}

      {preview && (
        <div className="max-h-[60dvh] space-y-4 overflow-y-auto">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div className="min-h-12 rounded-md border bg-card px-3 py-2">
              <p className="text-xs text-muted-foreground">{t('plants.merge.goes')}</p>
              <p className="break-words text-sm font-semibold">{preview.gone.name}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <div className="min-h-12 rounded-md border border-primary/60 bg-verdict-good-tint/50 px-3 py-2">
              <p className="text-xs text-muted-foreground">{t('plants.merge.stays')}</p>
              <p className="break-words text-sm font-semibold">{preview.keep.name}</p>
            </div>
          </div>

          {rows && (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">{t('plants.merge.varietyTitle')}</legend>
              {rows.map((row) => (
                <label key={row.varietyId} className={cn(ROW, 'cursor-pointer text-sm', selectedId === row.varietyId && 'border-primary/60')}>
                  <input
                    type="radio"
                    name="merge-variety"
                    checked={selectedId === row.varietyId}
                    onChange={() => setPicked(row.varietyId)}
                    disabled={merging}
                    className="h-4 w-4 accent-primary"
                  />
                  <span>{row.isPlaceholder || row.name === null ? t('plants.form.fields.notSure') : row.name}</span>
                </label>
              ))}
            </fieldset>
          )}

          <div className="space-y-1">
            <p className="text-sm font-medium">{t('plants.merge.movesTitle')}</p>
            {moves?.kind === 'lines' ? (
              <ul className="space-y-0.5 text-sm">{moves.lines.map((line) => <MoveLine key={line} line={line} />)}</ul>
            ) : moves ? (
              <p className="text-sm text-muted-foreground">{t('plants.merge.nothingToMove', { gone: moves.gone })}</p>
            ) : null}
          </div>

          <p className="text-sm text-muted-foreground">{t('plants.merge.nothingDeleted', { gone: preview.gone.name })}</p>
        </div>
      )}

      {failed && <p role="alert" className="whitespace-pre-line text-sm text-destructive"><FailureText text={failed} /></p>}

      <ActionRow onCancel={onClose} cancelLabel={t('plants.merge.cancel')} disabled={merging}>
        {preview && (
          <Button onClick={() => { void handleMerge(); }} loading={merging} disabled={merging}>
            {t('plants.merge.merge')}
          </Button>
        )}
      </ActionRow>
    </>
  );
};

/**
 * The merge sheet: the ask prompt, the pick step and the confirm sheet, one after the other in one
 * bottom sheet (a small box from sm up).
 *
 * @param props - MergePlantSheetProps.
 */
export const MergePlantSheet: React.FC<MergePlantSheetProps> = ({ plant, twins, mode, onClose, onMerged }) => {
  const { reactTranslate: t } = useTranslation();
  const { plants } = usePlantsContext();
  /** Which step shows; 'ask' only when opened for the prompt. */
  const [step, setStep] = React.useState<'ask' | 'pick' | 'confirm'>('confirm');
  /** The twin the gardener chose to keep (or the only one). */
  const [keepId, setKeepId] = React.useState<number | null>(null);
  /** True while the merge is on its way: the sheet cannot be closed. */
  const [busy, setBusy] = React.useState(false);
  const plantId = plant?.plant_obj_id ?? null;

  // A different plant or mode starts again at its first step.
  React.useEffect(() => {
    setBusy(false);
    if (plantId === null) return;
    const first = firstMergeStep(twins);
    setKeepId(first === 'confirm' ? twins[0].plant_obj_id : null);
    setStep(mode === 'ask' ? 'ask' : first === 'none' ? 'confirm' : first);
    // twins is rebuilt each render by the caller; the plant and mode say when to start again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId, mode]);

  /** The twin that stays, as a plant of the list. */
  const keep = twins.find((p) => p.plant_obj_id === keepId) ?? (plants ?? []).find((p) => p.plant_obj_id === keepId) ?? null;
  /** Leaves the ask prompt for the next step. */
  const afterAsk = (): void => {
    const first = firstMergeStep(twins);
    if (first === 'confirm') setKeepId(twins[0].plant_obj_id);
    setStep(first === 'none' ? 'confirm' : first);
  };
  const ask: EditAsk = { variant: twins.length > 1 ? 'several' : 'one', count: twins.length, twins };

  return (
    <Dialog open={plant !== null && twins.length > 0} onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className={SHEET_CLASS}>
        {/* The confirm step waits for the twin that stays; until then the box still needs a name for screen readers (B51). */}
        {plant && step === 'confirm' && !keep && <DialogTitle className="sr-only">{t('plants.merge.dialogLabel')}</DialogTitle>}
        {plant && step === 'ask' && <AskStep plant={plant} ask={ask} onMerge={afterAsk} onKeep={onClose} />}
        {plant && step === 'pick' && (
          <PickStep plant={plant} twins={twins} onPick={(id) => { setKeepId(id); setStep('confirm'); }} onCancel={onClose} />
        )}
        {plant && step === 'confirm' && keep && (
          <ConfirmStep key={`${plant.plant_obj_id}-${keep.plant_obj_id}`} gone={plant} keep={keep} onClose={onClose} onMerged={onMerged} onBusy={setBusy} />
        )}
      </DialogContent>
    </Dialog>
  );
};
